import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { UserCog } from "lucide-react";

/**
 * Dialog to create or edit a user (admin or seller).
 */
export function UserDialog({
  open,
  onOpenChange,
  editingUser,
  userForm,
  setUserForm,
  onSave,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="user-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-sky-900 text-xl flex items-center gap-2">
            <UserCog className="w-5 h-5 text-sky-600" />
            {editingUser ? "Editar usuario" : "Nuevo usuario"}
          </DialogTitle>
          <DialogDescription>
            {editingUser
              ? "Modificá los datos, rol o contraseña (opcional)."
              : "Creá un vendedor o administrador. Podrán entrar con este usuario."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-sky-900 font-medium">Usuario</Label>
            <Input
              value={userForm.username}
              disabled={!!editingUser}
              onChange={(e) =>
                setUserForm((f) => ({ ...f, username: e.target.value }))
              }
              placeholder="ej. juan.perez"
              className="border-sky-200"
              data-testid="user-username-input"
            />
            {editingUser && (
              <p className="text-xs text-sky-600/70 mt-1">
                El nombre de usuario no se puede cambiar.
              </p>
            )}
          </div>
          <div>
            <Label className="text-sky-900 font-medium">Nombre completo</Label>
            <Input
              value={userForm.full_name}
              onChange={(e) =>
                setUserForm((f) => ({ ...f, full_name: e.target.value }))
              }
              placeholder="ej. Juan Pérez"
              className="border-sky-200"
              data-testid="user-fullname-input"
            />
            <p className="text-xs text-sky-600/70 mt-1">
              Se muestra como "vendedor" en los clientes.
            </p>
          </div>
          <div>
            <Label className="text-sky-900 font-medium">Email</Label>
            <Input
              type="email"
              value={userForm.email}
              onChange={(e) =>
                setUserForm((f) => ({ ...f, email: e.target.value }))
              }
              placeholder="juan@agencia.com"
              className="border-sky-200"
              data-testid="user-email-input"
            />
          </div>
          <div>
            <Label className="text-sky-900 font-medium">Rol</Label>
            <Select
              value={userForm.role}
              onValueChange={(v) => setUserForm((f) => ({ ...f, role: v }))}
            >
              <SelectTrigger className="border-sky-200" data-testid="user-role-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="seller">
                  Vendedor (ve solo sus clientes)
                </SelectItem>
                <SelectItem value="admin">Administrador (ve todo)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sky-900 font-medium">
              {editingUser ? "Nueva contraseña (opcional)" : "Contraseña"}
            </Label>
            <Input
              type="password"
              value={userForm.password}
              onChange={(e) =>
                setUserForm((f) => ({ ...f, password: e.target.value }))
              }
              placeholder={
                editingUser ? "Dejar vacío para mantener" : "Mínimo 6 caracteres"
              }
              className="border-sky-200"
              data-testid="user-password-input"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 border-sky-200"
              data-testid="user-cancel-button"
            >
              Cancelar
            </Button>
            <Button
              onClick={onSave}
              className="flex-1 btn-sky text-white"
              data-testid="user-save-button"
            >
              {editingUser ? "Guardar cambios" : "Crear usuario"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
